import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import MonthlyIndicatorForm from '../components/MonthlyIndicatorForm.tsx';
import KeyResultProgress from '../components/KeyResultProgress.tsx';
import MonthlyIndicatorHistory from '../components/MonthlyIndicatorHistory.tsx';
import { addIndicator, listIndicators, listObjectives } from '../services/okrApi.ts';
import './Objectives.css';

interface KeyResult {
  id: number;
  objective_id: number;
  title: string;
  target_value: number;
  unit: string;
  status: 'active' | 'archived';
  current_value: number;
  progress_percent: number;
}

interface Objective {
  id: number;
  title: string;
  key_results: KeyResult[];
}

interface MonthlyIndicator {
  id: number;
  key_result_id: number;
  month: string;
  value: number;
}

const KeyResultDetail: React.FC = () => {
  const { id } = useParams();
  const keyResultId = Number(id);
  const [keyResult, setKeyResult] = useState<KeyResult | null>(null);
  const [objective, setObjective] = useState<Objective | null>(null);
  const [indicators, setIndicators] = useState<MonthlyIndicator[]>([]);
  const [loading, setLoading] = useState(true);

  const loadKeyResult = async () => {
    const objectives = await listObjectives();
    for (const obj of objectives) {
      const found = obj.key_results?.find((kr: KeyResult) => kr.id === keyResultId);
      if (found) {
        setKeyResult(found);
        setObjective({ id: obj.id, title: obj.title, key_results: obj.key_results });
        return;
      }
    }
  };

  const loadIndicators = async () => {
    if (!Number.isFinite(keyResultId)) return;
    const data = await listIndicators(keyResultId);
    setIndicators(data);
  };

  const refresh = async () => {
    setLoading(true);
    try {
      await Promise.all([loadKeyResult(), loadIndicators()]);
    } catch (error) {
      console.error('Erro ao carregar detalhes do Key Result:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keyResultId]);

  const handleAddIndicator = async (payload: { month: string; value: number }) => {
    await addIndicator(keyResultId, payload);
    await loadIndicators();
  };

  if (loading) {
    return (
      <div className="key-result-detail">
        <div className="page-header">
          <h1>Resultado</h1>
          <p>Carregando...</p>
        </div>
      </div>
    );
  }

  if (!keyResult) {
    return (
      <div className="key-result-detail">
        <div className="page-header">
          <h1>Resultado</h1>
          <p>Não encontrado.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="key-result-detail">
      <div className="page-header">
        <div>
          <h1>{keyResult.title}</h1>
          {objective && <p>Objetivo: {objective.title}</p>}
        </div>
        <div className="objective-actions">
          <Link
            className="secondary-button"
            to={objective ? `/objectives/${objective.id}` : '/objectives'}
          >
            Voltar
          </Link>
        </div>
      </div>

      <div className="key-result-detail-content">
        <KeyResultProgress
          currentValue={keyResult.current_value}
          targetValue={keyResult.target_value}
          unit={keyResult.unit}
          progressPercent={keyResult.progress_percent}
        />

        <div className="form-container">
          <h3>Registrar indicador mensal</h3>
          <MonthlyIndicatorForm onSubmit={handleAddIndicator} />
        </div>

        <h3>Histórico mensal</h3>
        <MonthlyIndicatorHistory indicators={indicators} unit={keyResult.unit} />
      </div>
    </div>
  );
};

export default KeyResultDetail;
